import { Fragment, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { scrollToElement } from 'lib/common';
import { Box, BoxRow, BoxSectionTitle } from 'components/common/box';
import {
  Answer,
  AnswerDeleteAll,
  ChaptersDropdownFilter
} from 'components/common/tasks';
import {
  ApiPostButton,
  EmptyIndicator,
  MoreOptions,
  SendToEmailButton
} from 'components/common';
import { FollowUsersDownloadAnswers } from 'components/courses/follow';

import type {
  Chapter,
  Course,
  CoursePermissions,
  TasksDataByUser,
  UserWithBasicData
} from 'shared-types';

interface Props {
  chapters: (Pick<Chapter, 'id' | 'title'> & { number: number })[];
  course: Pick<Course, 'id' | 'slug' | 'tasksIncludeReviews'> & {
    adminPermissions?: CoursePermissions[];
    isAdmin?: boolean;
    isOwnerOrSuperadmin?: boolean;
  };
  isUserPage: boolean;
  loading?: boolean;
  loggedUserHasEmail: boolean;
  onDataChange: () => Promise<void>;
  tasks: TasksDataByUser['tasks'];
  tasksAllowDownloading: boolean;
  tasksOrder: TasksDataByUser['tasksOrder'];
  user: UserWithBasicData;
  userTasksData: TasksDataByUser['users'][0];
}

const ProfileCoursesCourseTasks = ({
  chapters,
  course,
  isUserPage,
  loading,
  loggedUserHasEmail,
  onDataChange,
  tasks,
  tasksAllowDownloading,
  tasksOrder,
  user,
  userTasksData
}: Props) => {
  const { language, messages, theme } = useReduxState();

  const router = useRouter();
  const blockId = +router.query.blockId || null;

  const highlightedBlockElement = useRef<HTMLDivElement>(undefined);

  const [selectedChapterId, setSelectedChapterId] = useState(null);

  useEffect(() => {
    const answerEl = highlightedBlockElement.current;
    if (answerEl) {
      scrollToElement({
        element: answerEl,
        offset: -20,
        useBoundingClientRect: true
      });
      answerEl.style.transition = 'all 1s';
      answerEl.style.boxShadow = `0 0 10px 10px ${theme.successLightColor}`;
      setTimeout(() => {
        answerEl.style.boxShadow = '';
      }, 2000);
      setTimeout(() => {
        answerEl.style.transition = '';
      }, 3000);
    }
  }, [blockId]);

  const handleChapterChange = (selectedChapterId: number) =>
    setSelectedChapterId(selectedChapterId);

  const answers = userTasksData?.answers || null;

  const answersOrder = tasksOrder
    ? tasksOrder.filter(
        (taskId) => userTasksData?.answers[taskId] !== undefined
      )
    : null;

  const filteredOrder = answersOrder
    ? answersOrder.filter(
        (taskId) =>
          tasks &&
          tasks[taskId] &&
          answers &&
          answers[taskId] &&
          (!selectedChapterId || selectedChapterId === tasks[taskId].chapter.id)
      )
    : null;

  return (
    <div className="tasks">
      <Box
        headerContent={
          <div className="header-content">
            <div className="chapters-dropdown-filter">
              <ChaptersDropdownFilter
                chapters={chapters}
                onChange={handleChapterChange}
              />
            </div>

            {user && course && (isUserPage || tasksAllowDownloading) ? (
              <div className="download-summary">
                <MoreOptions category="lighter" styleContentButtons={false}>
                  <FollowUsersDownloadAnswers
                    courseId={course.id}
                    includeReviews={course.tasksIncludeReviews}
                    isAdmin={Boolean(isUserPage)}
                    isDropdown={true}
                    user={user}
                  />

                  {loggedUserHasEmail ? (
                    <ApiPostButton
                      data={{ language }}
                      icon="envelope"
                      isDropdown={true}
                      successMessage={messages.tasksSendAnswersSuccess}
                      text={messages.tasksSendAnswers}
                      url={`/admin/users/${user?.id}/courses/${course.id}/answers/send`}
                    />
                  ) : (
                    <SendToEmailButton
                      apiData={{ language }}
                      apiUrl={`/admin/users/${user?.id}/courses/${course.id}/answers/send`}
                      buttonText={messages.tasksSendAnswers}
                      id="answers"
                      isDropdown={true}
                      successMessage={messages.tasksSendAnswersSuccess}
                    />
                  )}

                  {course.isOwnerOrSuperadmin ||
                  (course.isAdmin &&
                    course.adminPermissions.includes('review-tasks')) ? (
                    <AnswerDeleteAll
                      answerIds={filteredOrder.map(
                        (blockId) => answers[blockId]?.id
                      )}
                      courseId={course.id}
                      onDataChange={onDataChange}
                    />
                  ) : null}
                </MoreOptions>
              </div>
            ) : null}
          </div>
        }
        headerContentWrap={true}
        loading={loading}
        padding={0}
        title={messages.tasksAnswers}
      >
        {answersOrder && answersOrder.length ? (
          <div className="answers">
            {filteredOrder && filteredOrder.length ? (
              filteredOrder.map((taskId, i, filteredTasksOrder) => {
                const task = tasks[taskId];
                const answer = answers[taskId];

                return (
                  <Fragment key={taskId}>
                    {i === 0 ||
                    task.chapter.number !==
                      tasks[filteredTasksOrder[i - 1]].chapter.number ? (
                      <BoxSectionTitle
                        title={`${task.chapter.number}. ${task.chapter.title}`}
                      />
                    ) : null}

                    <BoxRow borderColor={theme.lightGreyDark} padding={0}>
                      <div
                        className="answer"
                        {...(blockId && task.id === blockId
                          ? {
                              ref: (div) => {
                                highlightedBlockElement.current = div;
                              }
                            }
                          : {})}
                      >
                        <Answer
                          answer={answer}
                          courseId={course.id}
                          courseSlug={course.slug}
                          events={answer.events}
                          isAdmin={
                            course.isOwnerOrSuperadmin ||
                            (course.isAdmin &&
                              course.adminPermissions?.includes('review-tasks'))
                          }
                          onDataChange={onDataChange}
                          reviews={answer.reviews}
                          showPreview={true}
                          task={tasks[taskId]}
                          user={user}
                        />
                      </div>
                    </BoxRow>
                  </Fragment>
                );
              })
            ) : (
              <EmptyIndicator
                icon={['far', 'file-alt']}
                title={messages.tasksNoAnswers}
              />
            )}
          </div>
        ) : (
          <EmptyIndicator
            description={messages.tasksNoAnswersInfo}
            icon={['far', 'file-alt']}
            title={messages.tasksNoAnswers}
          />
        )}
      </Box>

      <style jsx>{`
        .header-content {
          align-items: center;
          display: flex;
        }
        .chapters-dropdown-filter {
          margin-right: 10px;
        }
        .answer {
          padding: 30px 15px;
        }
        @media (min-width: ${breakpoints.small}px) {
          .answer {
            padding: 40px 25px;
          }
        }
      `}</style>
    </div>
  );
};

export default ProfileCoursesCourseTasks;
